import { useCallback } from 'react';

import { expValEquals } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { useSubscriptionEvent } from '@confluence/graphql/entry-points/useSubscriptionEvent.ts';
import type { ConfluenceOnContentModified } from '@confluence/graphql/entry-points/subscriptionTypes.ts';
import { usePageContentId } from '@confluence/page-context';

import { refetchPageContent } from './refetchPageContent.ts';
import { patchIsMarkdownModeCache } from './useUpdateMarkdownMode.ts';

/**
 * Reacts to BE-fired `markdownModeUpdated` PubSub events on the active live
 * page:
 *   1. Refetch the collab-draft body so the converted ADF lands in Apollo.
 *   2. Patch `IsMarkdownModeQuery` so `useIsMarkdownMode` flips. The flip
 *      triggers a preset rebuild and an editor-view remount; the new view
 *      reads its `defaultValue` from the cache populated in step 1, so it
 *      never mounts with the stale pre-convert body.
 *
 * Echoes to the originator too. Their convert flow has already done both
 * steps, so the second refetch is a wasted network call (lands in the same
 * cache entry) and the second cache patch is an idempotent no-op.
 */
export const MarkdownModeUpdateSubscriber = () => {
	const [contentId] = usePageContentId();

	const onDataReceived = useCallback(
		(_event: string, response: ConfluenceOnContentModified) => {
			const isMarkdownMode = response?.markdownModeUpdated?.isMarkdownMode;
			if (!contentId || typeof isMarkdownMode !== 'boolean') {
				return;
			}

			// Always act on the hook's `contentId`, not `response.id`. The
			// subscription is server-side filtered to `contentId` already, so a
			// divergent `response.id` would mean a misdirected event or a stale
			// closure — either way we should not patch a different page's cache.
			//
			// Order matters: body first, then mode. A mode flip ahead of the
			// refetch remounts the editor (cache-first) onto stale body and
			// reintroduces the empty-editor window.
			//
			// Patch unconditionally once the refetch settles. `refetchPageContent`
			// swallows network errors and always resolves, so a silent body-fetch
			// failure still flips the mode — preferable to leaving the mode out
			// of sync with the BE, since NCS will stream the converted body in
			// shortly after.
			void refetchPageContent(contentId).then(() =>
				patchIsMarkdownModeCache(
					contentId,
					isMarkdownMode,
					expValEquals('cc-markdown-mode', 'isEnabled', true),
				),
			);
		},
		[contentId],
	);

	useSubscriptionEvent({
		subscriberId: 'markdownModeUpdateSubscriber',
		contentId,
		contentType: 'page',
		delta: 'markdownModeUpdated',
		skip:
			!contentId ||
			!expValEquals('cc-markdown-mode', 'isEnabled', true) ||
			!fg('cc-markdown-mode-pubsub'),
		onDataReceived,
	});

	return null;
};
